import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";

const PriceGuess = lazy(() => import("@/components/games/PriceGuess").then((m) => ({ default: m.PriceGuess })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Palworld Card Price Quiz — Higher or Lower";
const DESCRIPTION =
  "Free Palworld TCG price quiz: two real card printings, one call — is the second worth more or less? Build a streak and learn what drives card values.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Read the first card", text: "The left card shows its official scan, printed code, rarity and Pal Stars Index baseline value." },
  { name: "Judge the second card", text: "The right card hides its value; weigh its rarity code, set and card type against the card you can see." },
  { name: "Call higher or lower", text: "Pick worth more or worth less. A correct call extends your streak and the winning card carries over." },
  { name: "Learn the misses", text: "Every answer prints both values, so you learn which rarity codes and sets actually carry price." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld card price quiz?", a: "It is a free higher-or-lower game using real Palworld Official Card Game printings: compare two cards and call whether the hidden one is worth more or less than the one on show." },
  { q: "Where do the card values come from?", a: "From the Pal Stars Index baseline, which prices a card from its printed rarity code, set and card type. The live marketplace medians shown on the price hub are separate and update daily." },
  { q: "Which Palworld cards are worth the most?", a: "The chase printings: the SSS gold secret card, OSR over super rares, SSP textured parallels and SR full arts sit far above Commons and Uncommons, and Trial Deck TSP parallels also carry a premium." },
  { q: "Does the price quiz pay rewards?", a: "No. Streaks are cosmetic, badges unlock at 5 and 10 correct calls, and runs are capped at 25 per browser per UTC day." },
  { q: "Can the price quiz help me trade better?", a: "Yes. It drills the relationship between rarity code and value, which is exactly the judgement you need on the price hub and in card trades." },
];

export const Route = createFileRoute("/price-guess")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld card price quiz — higher or lower" },
      { property: "og:description", content: "Two real Palworld TCG printings, one call: guess which card carries the bigger value." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/price-guess" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/price-guess" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Card Price Guesser", "/price-guess"]]),
      toolSchema({ name: "Palworld Card Price Guesser", description: DESCRIPTION, path: "/price-guess" }),
      howToSchema({ name: "How to play the Palworld card price quiz", description: "Read the shown card, judge the hidden one, call higher or lower and learn the values.", path: "/price-guess", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/price-guess"),
    ],
  }),
  component: PriceGuessPage,
});

function PriceGuessPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · TCG game</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld card price quiz</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Higher or lower with real Palworld Official Card Game printings: one card shows its Pal Stars Index value, the
          other hides it, and every call teaches you which rarity codes and sets actually carry price.
        </p>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Higher or lower: Palworld card values</h2><p className="mt-2 text-sm text-muted-foreground">Loading cards…</p></section>}>
        <PriceGuess />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="price-guess" heading="Price quiz badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> live marketplace medians.</li>
          <li><Link to="/card-market" className="font-semibold text-primary hover:underline">Palworld card flipping game →</Link></li>
          <li><Link to="/cards" className="font-semibold text-primary hover:underline">Palworld TCG card database →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld card price quiz FAQ" />
    </div>
  );
}
